import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";

interface NewsSectionProps {
  news: NewsArticle[];
}

export default function NewsSection({ news }: NewsSectionProps) {
  const [featured, ...rest] = news;
  const tickerItems = [...news, ...news];

  return (
    <section className="mt-10">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">最新影视资讯</h2>
          <p className="mt-1 text-sm text-foreground-500">追踪影片动态、剧集资讯与幕后制作的第一现场</p>
        </div>
        <Link
          to="/news"
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-600"
        >
          更多资讯 <i className="ri-arrow-right-line" />
        </Link>
      </div>

      <div className="mb-4 flex items-center gap-3 overflow-hidden rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
        <span className="flex shrink-0 items-center gap-1 rounded bg-accent-500 px-2 py-1 text-xs font-semibold text-white">
          <i className="ri-flashlight-fill" /> 快讯
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div className="flex w-max animate-marquee gap-8">
            {tickerItems.map((item, i) => (
              <Link
                key={`${item.id}-${i}`}
                to={`/news/${item.id}`}
                className="whitespace-nowrap text-sm text-foreground-700 transition-colors hover:text-accent-600"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Link
            to={`/news/${featured.id}`}
            className="group relative flex h-64 cursor-pointer items-end overflow-hidden rounded-xl border border-background-200 md:h-80"
          >
            <img
              src={featured.cover}
              alt={featured.title}
              title={`${featured.title} - 秋霞影院资讯`}
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/45 to-transparent" />
            <div className="relative z-10 p-5 md:p-6">
              <span className="rounded bg-accent-500 px-2 py-0.5 text-xs font-semibold text-white">
                {featured.category}
              </span>
              <h3 className="clamp-2 mt-3 font-heading text-lg font-bold text-white md:text-xl">
                {featured.title}
              </h3>
              <p className="clamp-2 mt-2 hidden text-sm text-background-200 md:block">{featured.summary}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-background-300">
                <span>{featured.date}</span>
                <span>{featured.author}</span>
              </div>
            </div>
          </Link>
        </div>

        <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          {rest.slice(0, 5).map((item) => (
            <li key={item.id}>
              <Link
                to={`/news/${item.id}`}
                className="group flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-background-100"
              >
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent-500" />
                <div>
                  <h3 className="clamp-2 font-heading text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-400">{item.date}</p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}